import { Button, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import styles from './contacts.module.css';

/**
 * THE "we couldn't read this" state for the contact detail and thread pages — one line and a way out.
 *
 * It exists because both pages rendered `<PageLoader />` for `!entry`, and `useContactEntry` reports
 * `undefined` for a FAILED read exactly as it does for a pending one (the trap documented on
 * `useContactEntryQuery`). So a contact-book failure painted a full-page spinner that never resolved —
 * the same symptom reported on Android, reached a different way. The pages now read the real query
 * signals and land here.
 *
 * [canRetry] is false for the one case retrying cannot help: the read SUCCEEDED and simply carried no
 * contact. Shared 1:1 with the native `ContactReadError`, down to the copy and the `contacts.error.retry`
 * key, and it reuses the same centred `.empty` block `ContactsBody` already shows its error in — plus
 * `.readError`, which holds `PageLoader`'s own `min-height: 50vh` so replacing the loader does not
 * collapse the page.
 */
export function ContactReadError({
  canRetry,
  onRetry,
  detail,
  fullHeight = true,
}: {
  canRetry: boolean;
  onRetry: () => void;
  /** The server's own message, shown under the title — what the contacts and inbox LISTS already
   *  displayed. Omitted on the detail/thread/viewer surfaces, which never showed it. */
  detail?: string;
  /** The lists sit inside a page that already has a header and a search field above them, so they take
   *  the plain centred block; the detail, thread and viewer surfaces REPLACE a `PageLoader` and need its
   *  half-viewport floor so the page does not collapse. */
  fullHeight?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={fullHeight ? `${styles.empty} ${styles.readError}` : styles.empty}
      data-testid="contactReadError"
    >
      <Typography variant="p2">{t('contacts.error.title')}</Typography>
      {detail ? (
        <Typography variant="p3" color="secondary">
          {detail}
        </Typography>
      ) : null}
      {canRetry ? (
        <Button variant="outlined" onClick={onRetry} data-testid="contactReadErrorRetry">
          {t('contacts.error.retry')}
        </Button>
      ) : null}
    </div>
  );
}
